/* import __COLOCATED_TEMPLATE__ from './guidance-editor.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import type { Router } from '@ember/routing';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import type Store from '@ember-data/store';
import type AppService from 'embercom/services/app-service';
import type TaskSuggestion from 'embercom/models/content-service/task-suggestion';

interface Signature {
  Args: {
    taskSuggestion: TaskSuggestion;
    actionName?: string;
  };
}

export default class GuidanceEditor extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: Router;
  @service declare store: Store;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare permissionsService: any;

  @action
  async editGuidance() {
    if (!this.permissionsService.currentAdminCan('can_create_and_edit_bots')) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_create_and_edit_bots',
      );
      return;
    }

    let taskSuggestion = this.args.taskSuggestion;
    let existingGuidanceId = taskSuggestion.suggestionMetadata?.guidance_id;

    if (existingGuidanceId) {
      await this.navigateToExistingGuidance(existingGuidanceId);
    } else {
      await this.createAndNavigateToNewGuidance(taskSuggestion);
    }
  }

  async navigateToExistingGuidance(guidanceId: string) {
    // Note: The service expects guideline IDs to be prefixed with "guideline:"
    this.aiAgentGuidelineService.setOpenGuidelineId(`guideline:${guidanceId}`);

    // Transition to the guidance page
    this.router.transitionTo(this.route);
  }

  async createAndNavigateToNewGuidance(taskSuggestion: TaskSuggestion) {
    // Create a new guideline with the current guidance text
    try {
      let newGuidance = await this.createGuidance();

      this.notificationsService.notifyConfirmation(
        this.intl.t('automation.analyze.task-suggestions.guidance-created-successfully'),
      );

      // Note: The service expects guideline IDs to be prefixed with "guideline:"
      this.aiAgentGuidelineService.setOpenGuidelineId(`guideline:${newGuidance.id}`);

      // Save the guideline ID in the task suggestion metadata
      taskSuggestion.suggestionMetadata = taskSuggestion.suggestionMetadata || {};
      taskSuggestion.suggestionMetadata.guidance_id = newGuidance.id;

      // Wait for the task suggestion to be saved before transitioning
      await taskSuggestion.save();

      this.router.transitionTo(this.route);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('automation.analyze.task-suggestions.failed-to-create-guidance'),
      );
    }
  }

  @action async createGuidance() {
    let guidelineTitle = this.args.actionName
      ? this.intl.t('automation.analyze.task-suggestions.escalation-guidance-for', {
          actionName: this.args.actionName,
        })
      : this.intl.t('automation.analyze.task-suggestions.task-suggestion-escalation-guidance');

    let newGuidance = await this.aiAgentGuidelineService.createNewGuideline('routing', {
      title: guidelineTitle,
      prompt: this.guidanceTextRaw,
    });

    return newGuidance;
  }

  get guidanceTextHtml() {
    return this.args.taskSuggestion.guidanceDescription || this.placeholderText;
  }

  get guidanceTextRaw() {
    return this.args.taskSuggestion.guidanceRawDescription;
  }

  get route() {
    return this.appService.app.canUseFeature('fin-escalation-rules')
      ? 'apps.app.automation.fin-ai-agent.escalation'
      : 'apps.app.automation.fin-ai-agent.guidance';
  }

  get placeholderText() {
    return this.intl.t('automation.analyze.task-suggestions.guidance-placeholder');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TaskSuggestions::GuidanceEditor': typeof GuidanceEditor;
    'reporting/automation/fin-ai-agent/analyze/task-suggestions/guidance-editor': typeof GuidanceEditor;
  }
}
